import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiRotateCcw, FiMessageCircle, FiX, FiExternalLink, FiSmartphone, FiArrowLeft, FiArrowRight, FiSend } from 'react-icons/fi';
import useSessionQna from './useSessionQna';
import { hasInstructorReply, isAnswered, isInstructorReply } from './qnaHelpers';
import { ReactionButtons, ModeratedBody, QrPopover, sessionQnaPath, sessionQnaUrl } from './SessionQnaPanel';
import { ago } from '../dashboard/instructorHome/instructorHome';
import styles from './SessionQnaSplit.module.css';

const isUnanswered = (q) => !isAnswered(q);
const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

function ReplyBox({ posting, onSend, replyTo, onCancelReplyTo }) {
  const [value, setValue] = useState('');
  const send = async () => {
    if (!value.trim() || posting) return;
    await onSend(value);
    setValue('');
  };
  return (
    <form className={styles.composer} onSubmit={(e) => { e.preventDefault(); send(); }}>
      {replyTo && (
        <div className={styles.replyingTo}>
          <span>Replying to <b>{replyTo.authorName}</b></span>
          <button type="button" onClick={onCancelReplyTo} aria-label="Cancel replying to this message"><FiX size={14} /></button>
        </div>
      )}
      <textarea
        className={styles.composerInput}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
        placeholder="Write a reply..."
        aria-label="Write a reply"
        dir="auto"
        rows={3}
        maxLength={2000}
      />
      <div className={styles.composerActions}>
        <span className={styles.composerHint}>Students see it right away.</span>
        <button type="submit" className={styles.send} disabled={posting || !value.trim()}>
          <FiSend size={14} aria-hidden="true" />Send reply
        </button>
      </div>
    </form>
  );
}

/**
 * Instructor Session Q&A (design Q3): the questions on the left, the chosen
 * question with its replies and reply box on the right. Phones show the
 * question first and the list underneath.
 */
export default function SessionQnaSplit({ open, onClose, sessionId, subtitle, title = 'Session Q&A' }) {
  const {
    questions, isOpen, loading, error, posting, postReply, toggleReaction, editMessage, deleteMessage, setAnswered,
  } = useSessionQna(open ? sessionId : null);
  const [tab, setTab] = useState('open');
  const [pickedId, setPickedId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const detailRef = useRef(null);

  const sorted = useMemo(() => [...questions].sort(byNewest), [questions]);
  const openList = useMemo(() => sorted.filter(isUnanswered), [sorted]);
  const answeredList = useMemo(() => sorted.filter((q) => !isUnanswered(q)), [sorted]);
  const list = tab === 'open' ? openList : answeredList;
  const selected = list.find((q) => q.id === pickedId) || list[0] || null;
  const at = selected ? list.indexOf(selected) : -1;

  // A new first question shouldn't yank the instructor away mid-reply: only
  // fall back to the first item when the picked one is gone from this tab.
  useEffect(() => { if (pickedId && !list.some((q) => q.id === pickedId)) setPickedId(null); }, [list, pickedId]);

  if (!open) return null;

  const activeReplyTo = replyTo && replyTo.questionId === selected?.id ? replyTo : null;

  const pick = (q) => {
    setPickedId(q.id);
    if (window.matchMedia?.('(max-width: 768px)').matches) detailRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  };

  const send = async (body) => {
    const next = tab === 'open' ? (openList[at + 1] || openList[at - 1] || null) : null;
    await postReply(selected.id, body, { replyToId: activeReplyTo?.id || null });
    setReplyTo(null);
    if (next) setPickedId(next.id);
  };

  // Answered out loud: close it without a written reply, then move on.
  const markAnswered = async () => {
    const next = openList[at + 1] || openList[at - 1] || null;
    if ((await setAnswered(selected.id, true)) && next) setPickedId(next.id);
  };

  const popOut = () => {
    const w = window.open(sessionQnaPath(sessionId), `session-qna-${sessionId}`, 'popup=yes,width=420,height=700,resizable=yes,scrollbars=yes');
    if (w) { w.focus(); onClose?.(); }
  };

  const tabBtn = (key, label, n) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      className={`${styles.tab} ${tab === key ? styles.tabOn : ''}`}
      onClick={() => { setTab(key); setPickedId(null); }}
    >
      {label} {n}
    </button>
  );

  return (
    <section className={styles.card} aria-label={title}>
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden="true"><FiMessageCircle size={17} /></span>
        <div className={styles.titles}>
          <h3 className={styles.title}>{title}</h3>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
        {isOpen && <span className={styles.live}><span className={styles.dot} aria-hidden="true" />LIVE</span>}
        {!loading && openList.length > 0 && <span className={styles.openPill}>{openList.length} open</span>}
        <span className={styles.tools}>
          <button type="button" className={styles.iconBtn} onClick={() => setShowQr((v) => !v)} aria-label="Show QR code for mobile" aria-expanded={showQr} title="Open on phone (QR code)">
            <FiSmartphone size={16} />
          </button>
          <button type="button" className={`${styles.iconBtn} ${styles.hideSmall}`} onClick={popOut} aria-label="Open in separate window" title="Open in separate window">
            <FiExternalLink size={16} />
          </button>
          {onClose && (
            <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
              <FiX size={16} />
            </button>
          )}
        </span>
      </div>
      {showQr && <QrPopover url={sessionQnaUrl(sessionId)} onHide={() => setShowQr(false)} />}
      {!loading && !isOpen && <div className={styles.note}>Q&amp;A is closed for this session. You can still review the thread.</div>}
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.split}>
        <div className={styles.listPane}>
          <div className={styles.tabs} role="tablist" aria-label="Questions">
            {tabBtn('open', 'Open', openList.length)}
            {tabBtn('answered', 'Answered', answeredList.length)}
          </div>
          <div className={styles.list}>
            {loading && <div className={styles.empty}>Loading questions...</div>}
            {!loading && list.length === 0 && (
              <div className={styles.empty}>
                {tab === 'open'
                  ? (questions.length === 0 ? 'No questions yet. New ones show up here as students ask.' : 'All caught up.')
                  : 'Nothing answered yet.'}
              </div>
            )}
            {list.map((q) => (
              <button
                key={q.id}
                type="button"
                className={`${styles.item} ${selected?.id === q.id ? styles.itemOn : ''}`}
                onClick={() => pick(q)}
                aria-current={selected?.id === q.id ? 'true' : undefined}
              >
                <span className={`${styles.itemDot} ${isUnanswered(q) ? styles.itemDotOpen : ''}`} aria-hidden="true" />
                <span className={styles.itemMain}>
                  <span className={styles.itemTop}>
                    <b className={q.isAnonymous ? styles.anon : ''}>{q.authorName}</b>
                    <span className={styles.itemTime}>{ago(q.createdAt)}</span>
                  </span>
                  <span className={styles.itemBody} dir="auto">{q.body}</span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.detail} ref={detailRef}>
          {selected ? (
            <>
              <div className={styles.thread}>
                <div className={styles.msgMeta}>
                  <b className={selected.isAnonymous ? styles.anon : ''}>{selected.authorName}</b>
                  <span className={styles.itemTime}>{ago(selected.createdAt)}</span>
                  {isUnanswered(selected) && (
                    <button type="button" className={styles.markBtn} onClick={markAnswered}>
                      <FiCheck size={14} aria-hidden="true" />Mark answered
                    </button>
                  )}
                  {selected.answeredAt && !hasInstructorReply(selected) && (
                    <button type="button" className={styles.markBtn} onClick={() => setAnswered(selected.id, false)}>
                      <FiRotateCcw size={13} aria-hidden="true" />Reopen
                    </button>
                  )}
                </div>
                <ModeratedBody message={selected} targetType="questions" canModerate onEdit={editMessage} onDelete={deleteMessage}>
                  <ReactionButtons
                    reactions={selected.reactions}
                    myReaction={selected.myReaction}
                    disabled={!isOpen}
                    onReact={(type) => toggleReaction('questions', selected.id, type)}
                  />
                </ModeratedBody>
                {(selected.replies || []).length > 0 && (
                  <div className={styles.replies}>
                    {selected.replies.map((r) => (
                      <div key={r.id} className={styles.reply}>
                        <div className={styles.msgMeta}>
                          <b>{r.authorName}</b>
                          {isInstructorReply(r) && <span className={styles.badge}>Instructor</span>}
                          <span className={styles.itemTime}>{ago(r.createdAt)}</span>
                        </div>
                        {r.replyToId && selected.replies.some((x) => x.id === r.replyToId) && (
                          <div className={styles.inReplyTo}>↪ replying to {selected.replies.find((x) => x.id === r.replyToId).authorName}</div>
                        )}
                        <ModeratedBody message={r} targetType="replies" canModerate onEdit={editMessage} onDelete={deleteMessage}>
                          <ReactionButtons
                            reactions={r.reactions}
                            myReaction={r.myReaction}
                            disabled={!isOpen}
                            onReact={(type) => toggleReaction('replies', r.id, type)}
                          />
                        </ModeratedBody>
                        {isOpen && <button type="button" className={styles.replyLink} onClick={() => setReplyTo(r)}>Reply</button>}
                      </div>
                    ))}
                  </div>
                )}
                {isUnanswered(selected) && (selected.replies || []).length === 0 && <p className={styles.hint}>No replies yet.</p>}
                {selected.answeredAt && !hasInstructorReply(selected) && <p className={styles.hint}>Marked answered, no written reply.</p>}
              </div>
              <div className={styles.bottom}>
                {isOpen && <ReplyBox key={selected.id} posting={posting} onSend={send} replyTo={activeReplyTo} onCancelReplyTo={() => setReplyTo(null)} />}
                <div className={styles.stepRow}>
                  <button type="button" className={styles.step} onClick={() => setPickedId(list[at - 1].id)} disabled={at <= 0}>
                    <FiArrowLeft size={14} aria-hidden="true" />Previous
                  </button>
                  <button type="button" className={styles.step} onClick={() => setPickedId(list[at + 1].id)} disabled={at >= list.length - 1}>
                    Next question<FiArrowRight size={14} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className={styles.empty}>{loading ? '' : 'Pick a question to reply.'}</div>
          )}
        </div>
      </div>
    </section>
  );
}
